import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Pencil, 
  Plus, 
  FileText, 
  Search,
  Layers, 
  Table, 
  LayoutGrid, 
  Laptop, 
  Box, 
  Server, 
  Monitor, 
  ShieldCheck, 
  MapPin, 
  AlertTriangle, 
  CheckCircle2, 
  Hash,
  User,
  Code2
} from 'lucide-react';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';
import { useAssetStore, Asset } from '../../../store/assetStore';
import { useTicketStore } from '../../../store/ticketStore';
import { ITSMAssetDetailView } from './AssetDetailView';
import { DataTable, type Column } from '../../../components/DataTable';
import { Pagination } from '../../../components/Pagination';
import { UserAvatar } from '../../../components/UserAvatar';
import { cn } from '../../../lib/utils';


export function AssetRegistry() {
  const { assets, isLoading, error, fetchAssets } = useAssetStore();
  const { categories: categoryRows, fetchCategories } = useAssetCatalogStore();
  // Filter pills: every category in assets.asset_categories plus any category an asset uses.
  const CATEGORIES = ['All', ...Array.from(new Set([...categoryRows.map((c) => c.name), ...assets.map((a) => a.category).filter((c): c is string => !!c)])).sort()];
  const { tickets, fetchTickets } = useTicketStore();
  const navigate = useNavigate();

  // View Mode: 'card' (default) or 'table'
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');

  const [catFilter, setCatFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  useEffect(() => {
    fetchAssets();
    fetchTickets();
    fetchCategories();
  }, [fetchAssets, fetchTickets, fetchCategories]);

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, catFilter, pageSize, viewMode]);

  const filteredAssets = useMemo(() => {
    return assets.filter((a) => {
      const matchCat = catFilter === 'All' || a.category === catFilter;
      const nameMatch = a.name?.toLowerCase().includes(search.toLowerCase()) || false;
      const codeMatch = a.asset_code?.toLowerCase().includes(search.toLowerCase()) || false;
      const modelMatch = a.model?.toLowerCase().includes(search.toLowerCase()) || false;
      const serialMatch = a.serial_number?.toLowerCase().includes(search.toLowerCase()) || false;
      const matchSearch = !search || nameMatch || codeMatch || modelMatch || serialMatch;
      return matchCat && matchSearch;
    });
  }, [assets, catFilter, search]);

  const totalItems = filteredAssets.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (validPage - 1) * pageSize;
  const paginatedAssets = filteredAssets.slice(startIndex, startIndex + pageSize);

  // Helper function for asset icon by category / type
  const getAssetCategoryIcon = (type?: string, category?: string) => {
    const t = (type || category || '').toLowerCase();
    if (t.includes('software') || t.includes('application') || t.includes('code') || t.includes('microservice')) return Code2;
    if (t.includes('laptop') || t.includes('macbook') || t.includes('thinkpad') || t.includes('dell')) return Laptop;
    if (t.includes('server') || t.includes('cloud')) return Server;
    if (t.includes('monitor') || t.includes('display')) return Monitor;
    if (t.includes('license')) return ShieldCheck;
    return Box;
  };

  // Status badge styling helper
  const getStatusStyle = (status?: string) => {
    const s = (status || '').toLowerCase().trim();
    if (s.includes('in stock') || s.includes('in_stock') || s.includes('available')) {
      return {
        badge: 'bg-info/15 text-info-strong border-info/30',
        dot: 'bg-info',
        borderAccent: 'border-l-sky-500',
      };
    }
    if (s.includes('assigned') || s.includes('active') || s.includes('in use') || s.includes('in_use')) {
      return {
        badge: 'bg-success/15 text-success-strong border-success/30',
        dot: 'bg-success',
        borderAccent: 'border-l-emerald-500',
      };
    }
    if (s.includes('maintenance') || s.includes('repair') || s.includes('hold')) {
      return {
        badge: 'bg-warning/15 text-warning-strong border-warning/30',
        dot: 'bg-warning',
        borderAccent: 'border-l-amber-500',
      };
    }
    if (s.includes('lost') || s.includes('damaged')) {
      return {
        badge: 'bg-destructive/15 text-destructive-strong border-destructive/30',
        dot: 'bg-destructive',
        borderAccent: 'border-l-rose-500',
      };
    }
    if (s.includes('retired') || s.includes('disposed')) {
      return {
        badge: 'bg-muted text-muted-foreground border-border',
        dot: 'bg-muted-foreground',
        borderAccent: 'border-l-slate-400',
      };
    }
    return {
      badge: 'bg-secondary text-secondary-foreground border-border',
      dot: 'bg-muted-foreground',
      borderAccent: 'border-l-border',
    };
  };

  // Columns definition for DataTable row columns method
  const columns = useMemo<Column<Asset>[]>(() => [
    {
      id: 'sNo',
      header: 'S.No',
      width: '60px',
      cell: (_row) => {
        const index = paginatedAssets.indexOf(_row);
        return <span className="font-mono text-muted-foreground">{startIndex + (index >= 0 ? index : 0) + 1}</span>;
      },
    },
    {
      id: 'assetCode',
      header: 'Asset_ID',
      width: '130px',
      sortable: true,
      sortAccessor: (a) => a.asset_code,
      cell: (asset) => (
        <div className="flex items-center gap-2 font-mono font-semibold text-primary group-hover:underline">
          {asset.images && asset.images[0] && (
            <img
              src={asset.images[0]}
              alt=""
              className="size-6 rounded border border-border object-cover shrink-0"
            />
          )}
          <span>{asset.asset_code}</span>
        </div>
      ),
    },
    {
      id: 'assignedPerson',
      header: 'Assigned_Person',
      width: '180px',
      sortable: true,
      sortAccessor: (a) => a.assignedToName || '',
      cell: (asset) => (
        <div className="flex items-center gap-2 font-medium text-foreground">
          {asset.assignedToName ? (
            <>
              <UserAvatar name={asset.assignedToName} size="xs" />
              <span className="truncate">{asset.assignedToName}</span>
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      width: '110px',
      sortable: true,
      sortAccessor: (a) => a.type || a.category || '',
      cell: (asset) => (
        <span className="text-muted-foreground">{asset.type || asset.category || '—'}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '150px',
      sortable: true,
      sortAccessor: (a) => a.status,
      cell: (asset) => {
        const openCount = tickets.filter(
          (t) =>
            (t.assetId === asset.id ||
              (asset.id && String(t.assetId) === String(asset.id)) ||
              (asset.asset_code && t.assetTag?.toLowerCase() === asset.asset_code?.toLowerCase())) &&
            t.status !== 'resolved' &&
            t.status !== 'closed'
        ).length;
        const getStatusBadgeColor = (status?: string) => {
          const s = (status || '').toLowerCase().trim();
          if (s.includes('in stock') || s.includes('available')) {
            return 'bg-info text-info-foreground shadow-2xs';
          }
          if (s.includes('assigned') || s.includes('in use') || s.includes('active')) {
            return 'bg-success text-success-foreground shadow-2xs';
          }
          if (s.includes('maintenance') || s.includes('repair') || s.includes('service')) {
            return 'bg-warning text-warning-foreground shadow-2xs';
          }
          if (s.includes('retired')) {
            return 'bg-slate-500 text-white shadow-2xs';
          }
          if (s.includes('lost') || s.includes('damaged')) {
            return 'bg-destructive text-destructive-foreground shadow-2xs';
          }
          if (s.includes('disposed')) {
            return 'bg-slate-600 text-white shadow-2xs';
          }
          return 'bg-secondary text-secondary-foreground shadow-2xs';
        };

        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                'inline-flex items-center justify-center rounded px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
                getStatusBadgeColor(asset.status)
              )}
            >
              {asset.status || 'Active'}
            </span>
            {openCount > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/support/tickets?assetTag=${asset.asset_code}`);
                }}
                className="inline-flex items-center gap-1 text-xs text-warning-strong bg-warning/10 hover:bg-warning/10 px-1.5 py-0.5 rounded font-mono font-semibold border border-warning/40 transition-colors cursor-pointer"
                title="Click to view open support tickets"
              >
                ⚠️ {openCount} {openCount === 1 ? 'ticket' : 'tickets'}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'serialNumber',
      header: 'Serial No.',
      width: '160px',
      sortable: true,
      sortAccessor: (a) => a.serial_number || (a.specifications?.version ? `v${a.specifications.version}` : ''),
      cell: (asset) => (
        <span className="font-mono text-foreground">
          {asset.serial_number || (asset.specifications?.version ? `v${asset.specifications.version}` : '—')}
        </span>
      ),
    },
    {
      id: 'model',
      header: 'Model',
      sortable: true,
      sortAccessor: (a) => a.name || a.model || '',
      cell: (asset) => (
        <div>
          <span className="font-semibold text-foreground">{asset.name || asset.model}</span>
          {asset.model && asset.name !== asset.model && (
            <span className="text-muted-foreground ml-1.5 font-mono text-xs">({asset.model})</span>
          )}
          {((asset.invoices && asset.invoices.length > 0) || asset.invoiceNumber) && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-success bg-success/10 px-1.5 py-0.5 rounded font-mono font-semibold">
              📄 Invoiced
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'issues',
      header: 'Issues?',
      width: '80px',
      align: 'center',
      cell: (asset) => (
        <span className="font-medium text-foreground">{
          tickets.some((t) => (String(t.assetId) === String(asset.id) || (asset.asset_code && t.assetTag?.toLowerCase() === asset.asset_code.toLowerCase())) && t.status !== 'resolved' && t.status !== 'closed') ? 'Yes' : 'No'
        }</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '50px',
      align: 'center',
      cell: (asset) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}?edit=true`);
          }}
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors cursor-pointer"
          title="Edit Asset Specs In-Place"
        >
          <Pencil className="size-3.5" />
        </button>
      ),
    },
  ], [startIndex, paginatedAssets, tickets, navigate]);

  return (
    <div className="p-4 space-y-4 bg-background min-h-full text-foreground text-xs font-sans flex flex-col justify-between">
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="space-y-4">
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HEADER SECTION                                                 */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HEADER SECTION CARD */}
        <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
              <Layers className="size-4.5" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <h1 className="tracking-tight text-foreground font-display text-page-title font-semibold truncate">
                Asset Management
              </h1>
              <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">
                {catFilter === 'All' ? 'Hardware & Software Inventory' : `${catFilter} Inventory`} ({totalItems} total tracked assets)
              </p>
            </div>
          </div>

          {/* Top Right Action Button: Add Asset */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate('/assets/new')}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-primary px-4 text-xs font-semibold text-primary-foreground shadow transition-colors hover:bg-primary/90 cursor-pointer"
            >
              <Plus className="size-4" />
              Add Asset
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FILTER TOOLBAR: SEARCH, CATEGORY DROPDOWN & VIEW MODE SWITCHER */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search assets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-card pl-8 pr-3 text-xs text-foreground shadow-2xs placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring"
              />
            </div>

            {/* Category Filter Dropdown */}
            <div className="relative w-full sm:w-60">
              <select
                value={catFilter}
                onChange={(e) => setCatFilter(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-card px-3 text-xs font-medium text-foreground shadow-2xs outline-none focus:ring-1 focus:ring-ring cursor-pointer"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c === 'All' ? 'All Categories' : c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* View Mode Switcher: Cards vs Table */}
          <div className="flex items-center rounded-lg border border-border bg-card p-0.5 shadow-2xs shrink-0 self-end sm:self-auto">
            <button
              onClick={() => setViewMode('card')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer',
                viewMode === 'card'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              title="Card View (1 Card per Row)"
            >
              <LayoutGrid className="size-3.5" />
              <span>Cards</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer',
                viewMode === 'table'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              title="Table View (Row Columns)"
            >
              <Table className="size-3.5" />
              <span>Table</span>
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MAIN VIEW CONTENT: 1 CARD PER ROW (DEFAULT) OR DATA TABLE      */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {viewMode === 'card' ? (
          /* ───────────────────────────────────────────────────────────── */
          /* CARD VIEW: 1 CARD PER ROW (HORIZONTAL WIDE LAYOUT)            */
          /* ───────────────────────────────────────────────────────────── */
          <div className="space-y-4">
            {isLoading && assets.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground shadow-xs">
                <div className="flex items-center justify-center gap-2">
                  <span className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                  Loading asset registry records...
                </div>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground shadow-xs">
                <FileText className="mx-auto size-8 text-muted-foreground/50 mb-2" />
                No assets found matching the search criteria.
              </div>
            ) : (
              <div className="space-y-3">
                {paginatedAssets.map((asset, index) => {
                  const statusStyle = getStatusStyle(asset.status);
                  const AssetIcon = getAssetCategoryIcon(asset.type, asset.category ?? undefined);

                  const openTicketsCount = tickets.filter(
                    (t) =>
                      (t.assetId === asset.id ||
                        (asset.id && String(t.assetId) === String(asset.id)) ||
                        (asset.asset_code && t.assetTag?.toLowerCase() === asset.asset_code?.toLowerCase())) &&
                      t.status !== 'resolved' &&
                      t.status !== 'closed'
                  ).length;

                  return (
                    <div
                      key={asset.id}
                      onClick={() => {
                        navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}`);
                      }}
                      className={cn(
                        'group relative flex flex-col lg:flex-row lg:items-center justify-between gap-4 rounded-lg border border-border bg-card p-4 shadow-xs hover:shadow-md hover:border-primary/50 transition-all cursor-pointer border-l-4',
                        statusStyle.borderAccent
                      )}
                    >
                      {/* Left: S.No, Thumbnail, Asset ID, Badges, Name/Model */}
                      <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                        {/* S.No badge */}
                        <span className="text-xs font-mono text-muted-foreground w-6 text-center shrink-0 hidden sm:inline-block">
                          {startIndex + index + 1}
                        </span>

                        {/* Thumbnail / Icon */}
                        <div className="size-12 rounded-lg border border-border bg-muted/30 flex items-center justify-center shrink-0 overflow-hidden">
                          {asset.images && asset.images[0] ? (
                            <img
                              src={asset.images[0]}
                              alt=""
                              className="size-full object-cover"
                            />
                          ) : (
                            <AssetIcon className="size-6 text-muted-foreground" />
                          )}
                        </div>

                        {/* Main Info */}
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-semibold text-primary group-hover:underline">
                              {asset.asset_code}
                            </span>
                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-muted text-muted-foreground border border-border/70">
                              {asset.type || asset.category || 'Asset'}
                            </span>
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border',
                                statusStyle.badge
                              )}
                            >
                              <span className={cn('size-1.5 rounded-full', statusStyle.dot)} />
                              {asset.status || 'Active'}
                            </span>
                          </div>

                          <div className="flex items-baseline gap-2 flex-wrap">
                            <h3 className="text-foreground group-hover:text-primary transition-colors font-display text-card-title">
                              {asset.name || asset.model || 'Untitled Asset'}
                            </h3>
                            {asset.model && asset.name !== asset.model && (
                              <span className="text-xs text-muted-foreground font-mono">
                                ({asset.model})
                              </span>
                            )}
                            {asset.manufacturer && (
                              <span className="text-xs text-muted-foreground">
                                • Mfg: {asset.manufacturer}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Middle Metadata (Assigned User, Serial Number, Location) */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-5 px-3 py-2 sm:py-0 rounded-lg sm:rounded-none bg-muted/30 sm:bg-transparent border sm:border-0 border-border/60 text-xs shrink-0">
                        {/* Assigned Person */}
                        <div className="flex items-center gap-2 min-w-[130px]">
                          <span className="text-muted-foreground text-xs">User:</span>
                          <div className="flex items-center gap-1.5 min-w-0 font-medium text-foreground">
                            {asset.assignedToName ? (
                              <>
                                <UserAvatar name={asset.assignedToName} size="xs" />
                                <span className="truncate">{asset.assignedToName}</span>
                              </>
                            ) : (
                              <span className="text-muted-foreground">Unassigned</span>
                            )}
                          </div>
                        </div>

                        {/* Serial Number / Version */}
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <span className="text-muted-foreground text-xs">
                            {asset.type?.toLowerCase() === 'software' || asset.category?.toLowerCase() === 'software' ? 'Ver:' : 'Serial:'}
                          </span>
                          <span className="font-mono text-xs font-semibold text-foreground truncate">
                            {asset.type?.toLowerCase() === 'software' || asset.category?.toLowerCase() === 'software'
                              ? (asset.specifications?.version ? `v${asset.specifications.version}` : asset.serial_number || '—')
                              : (asset.serial_number || '—')}
                          </span>
                        </div>

                        {/* Location / Service */}
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <span className="text-muted-foreground text-xs">
                            {asset.type?.toLowerCase() === 'software' || asset.category?.toLowerCase() === 'software' ? 'Service:' : 'Loc:'}
                          </span>
                          <span className="text-foreground truncate text-xs font-mono">
                            {asset.type?.toLowerCase() === 'software' || asset.category?.toLowerCase() === 'software'
                              ? (asset.specifications?.service || asset.notes || '—')
                              : ([asset.department, asset.location].filter(Boolean).join(' • ') || '—')}
                          </span>
                        </div>
                      </div>

                      {/* Right Actions & Badges */}
                      <div className="flex items-center justify-between lg:justify-end gap-3 shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-border/60">
                        <div className="flex items-center gap-2">
                          {openTicketsCount > 0 ? (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/support/tickets?assetTag=${asset.asset_code}`);
                              }}
                              className="inline-flex items-center gap-1 text-xs text-warning-strong bg-warning/10 hover:bg-warning/10 px-2 py-0.5 rounded font-mono font-semibold border border-warning/40 transition-colors cursor-pointer"
                              title="Click to view open support tickets"
                            >
                              <AlertTriangle className="size-3" /> {openTicketsCount} {openTicketsCount === 1 ? 'ticket' : 'tickets'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              <CheckCircle2 className="size-3 text-success" /> No issues
                            </span>
                          )}

                          {((asset.invoices && asset.invoices.length > 0) || asset.invoiceNumber) && (
                            <span className="inline-flex items-center gap-1 text-xs text-success bg-success/10 px-1.5 py-0.5 rounded font-mono font-semibold">
                              📄 Invoiced
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}?edit=true`);
                            }}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer"
                            title="Edit Asset Specs In-Place"
                          >
                            <Pencil className="size-3.5" />
                          </button>

                          <span className="text-primary font-semibold group-hover:translate-x-0.5 transition-transform text-xs pl-1">
                            View &rarr;
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* CARD VIEW PAGINATION TOOLBAR */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 rounded-lg border border-border bg-card px-5 py-3.5 text-xs text-muted-foreground shadow-xs">
              <div className="flex items-center gap-2">
                <span>Assets per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-medium cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={80}>80</option>
                </select>
              </div>

              <Pagination
                page={validPage}
                pageCount={totalPages}
                onPageChange={setCurrentPage}
                total={totalItems}
                pageSize={pageSize}
              />
            </div>
          </div>
        ) : (
          /* ───────────────────────────────────────────────────────────── */
          /* TABLE VIEW: ROW COLUMNS METHOD (DataTable Component)          */
          /* ───────────────────────────────────────────────────────────── */
          <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
            <DataTable
              columns={columns}
              data={paginatedAssets}
              keyExtractor={(a) => a.id}
              isLoading={isLoading && assets.length === 0}
              onRowClick={(asset) => {
                navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}`);
              }}
              footer={
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border px-5 py-3.5 bg-card text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-medium cursor-pointer"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <Pagination
                    page={validPage}
                    pageCount={totalPages}
                    onPageChange={setCurrentPage}
                    total={totalItems}
                    pageSize={pageSize}
                  />
                </div>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
